import { Box } from '@mantine/core'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { NavLink, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { clsx } from '@mantine/styles'

import { SignInCard } from '../SignInCard/SignInCard'
import classes from '../SignInCard/SignInCard.module.scss'

import { Button, FormField, Spinner, TextInput } from '@percent/lemonade'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { useMutation } from '@percent/workplace-giving/common/hooks/useMutation/useMutation'
import { ssoLogin } from '@percent/workplace-giving/api/auth/ssoLogin/ssoLogin'
import { config } from '@percent/workplace-giving/config/config'
import { useSharedValidationRules } from '@percent/workplace-giving/common/hooks/useSharedValidationRules/useSharedValidationRules'

type LocationProps = {
  from: Location
}

export function SSOSignIn() {
  const { t } = useTranslation()
  const locationState = useLocation().state as LocationProps | null
  const { validateEmail } = useSharedValidationRules()

  const [errorMessage, setErrorMessage] = useState('')
  const [awaitingRedirect, setAwaitingRedirect] = useState(false)

  const { mutateAsync } = useMutation({
    mutationFn: ssoLogin,
    onSuccess: res => {
      if (res.result === 'error') {
        setErrorMessage(t('workplace_giving.errors.signInWithSSOErrors.partnerNotConfigured'))
        setAwaitingRedirect(false)

        return
      }
      window.location.href = res.data.loginUrl
    },
    onError: () => {
      setErrorMessage(t('workplace_giving.errors.unexpected'))
      setAwaitingRedirect(false)
    }
  })

  const { errors, values, handleChange, handleSubmit, handleBlur, touched } = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema: () =>
      Yup.object().shape({
        email: validateEmail()
      }),
    onSubmit: ({ email }) => {
      setAwaitingRedirect(true)
      let redirectUrl

      if (locationState) {
        const { from } = locationState
        redirectUrl = config.urls.appUrl + (from.pathname ?? AppRoute.HOME) + (from.search ?? '')
      }

      return mutateAsync({ email, redirectUrl })
    }
  })

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage('')
    handleChange(event)
  }

  return (
    <SignInCard title={t('workplace_giving.login.signInWithSSO')}>
      {awaitingRedirect ? (
        <Box className={classes.CenteredFlexBox}>
          <Spinner size="l" />
        </Box>
      ) : (
        <Box component="form" className={classes.Form} onSubmit={handleSubmit}>
          <FormField
            label={t('workplace_giving.login.email.label')}
            status={errorMessage ? 'danger' : touched.email && errors.email ? 'danger' : 'default'}
            statusMessage={errors.email || errorMessage}
            data-testid="email"
            necessity="required"
          >
            <TextInput
              name="email"
              value={values.email}
              placeholder={t('workplace_giving.login.email.placeholder')}
              onBlur={handleBlur}
              onChange={handleInputChange}
              borderRadiiSize="large"
            />
          </FormField>

          <Button variant="primary" type="submit" shape="oval" disabled={awaitingRedirect}>
            {t('workplace_giving.login.signInWithSSO')}
          </Button>

          <Box className={clsx(classes.LinksWrapper, classes.LinksWrapperCentered)}>
            <NavLink to={AppRoute.SIGN_IN} state={locationState}>
              {t('workplace_giving.login.signInWithEmail')}
            </NavLink>
          </Box>
        </Box>
      )}
    </SignInCard>
  )
}
